import React, { useRef, useState } from 'react'

import { theme } from '@acorns/web-components'
import { useAnalyticsClient } from '@acorns/web-utils'
import { ApolloError } from 'apollo-client'
import styled from 'styled-components'

import {
  Performance as PerformanceType,
  RecapItemsFragment,
} from 'generated/types'
import { PUBLISHING_RECAP_PAGE } from 'utils/testing/testIDs'

import { ACTIONS, shareAnalyticsOptions } from './analytics'
import { Contributions } from './Contributions/Contributions'
import { Error } from './Error/Error'
import { Gifts } from './Gifts/Gifts'
import { parseRecapData } from './helpers'
import { Intro } from './Intro/Intro'
import { Loading } from './Loading/Loading'
import { Performance } from './Performance/Performance'
import { Potential } from './Potential/Potential'
import { PublishingContext } from './PublishingContext'
import { MobileShareButton } from './ShareRecap/MobileShareButton'
import { ShareRecapController } from './ShareRecap/ShareRecapController'
import { RECAP_XS_BREAKPOINT } from './styles'
import { TableOfContent } from './TableOfContent/TableOfContent'
import { TitleBar } from './TitleBar/TitleBar'

const Container = styled.div`
  align-items: center;
  background-color: ${theme.colors.charcoal};
  display: flex;
  flex-direction: column;
  left: 0;
  margin: auto;
  min-width: 320px;
  overflow-x: hidden;
  position: absolute;
  width: 100%;

  @media screen and (max-width: ${RECAP_XS_BREAKPOINT}px) {
    width: 100%;
  }
`

export const PublishingPage = ({
  data,
  error,
  loading,
  isCustodian = false,
  smartDepositAmount,
}: {
  data: RecapItemsFragment | undefined | null
  loading: boolean
  error: ApolloError | undefined
  isCustodian?: boolean
  smartDepositAmount?: number | undefined
}): React.ReactElement => {
  const analytics = useAnalyticsClient()

  const [showShareModal, setShowShareModal] = useState(false)

  const tableOfContentRef = useRef<HTMLDivElement>(null)
  const handleScrollNext = () => {
    if (tableOfContentRef.current) {
      window.scrollTo({
        behavior: 'smooth',
        top: tableOfContentRef.current.offsetTop - 66,
      })
    }
  }

  if (error) {
    return <Error />
  }

  if (!data || loading) {
    return <Loading />
  }

  const {
    age,
    ageOfTransfer,
    beneficiaryId,
    beneficiaryName,
    contributionByOthers,
    currentBalance,
    custodianContribution,
    custodianNickname,
    endsAt,
    gifts,
    investmentAccountId,
    performance,
    publicEnabled,
    publicUrl,
    recapId,
    recurringInvestmentSettings,
    startsAt,
  } = parseRecapData(data || undefined)

  const handleShareClick = () => {
    setShowShareModal(true)
    analytics.track(ACTIONS.BUTTON_TAPPED, shareAnalyticsOptions)
  }

  return (
    <PublishingContext.Provider value={{ isCustodian }}>
      <Container data-testid={PUBLISHING_RECAP_PAGE}>
        {isCustodian ? (
          <TitleBar
            beneficiaryId={beneficiaryId}
            beneficiaryName={beneficiaryName}
            onShowShareModal={handleShareClick}
          />
        ) : null}
        <Intro
          beneficiaryId={beneficiaryId}
          beneficiaryName={beneficiaryName}
          endsAt={endsAt}
          handleScrollNext={handleScrollNext}
          startsAt={startsAt}
        />
        <div ref={tableOfContentRef} />
        <TableOfContent
          beneficiaryId={beneficiaryId}
          custodianNickname={custodianNickname}
        />
        <Contributions
          beneficiaryId={beneficiaryId}
          custodianContribution={custodianContribution}
          custodianNickname={custodianNickname}
        />
        <Gifts
          beneficiaryId={beneficiaryId}
          beneficiaryName={beneficiaryName}
          contributionByOthers={contributionByOthers}
          gifts={gifts}
          investmentAccountId={investmentAccountId}
        />
        {performance ? (
          <Performance
            {...(performance as PerformanceType)}
            beneficiaryId={beneficiaryId}
            endsAt={endsAt}
            startsAt={startsAt}
          />
        ) : null}
        <Potential
          age={age}
          ageOfTransfer={ageOfTransfer}
          beneficiaryId={beneficiaryId}
          currentBalance={currentBalance}
          recurringInvestmentSettings={recurringInvestmentSettings}
          smartDepositAmount={smartDepositAmount}
        />
        {isCustodian ? (
          <MobileShareButton onShowShareModal={handleShareClick} />
        ) : null}
        <ShareRecapController
          showModal={showShareModal}
          onShowShareModal={setShowShareModal}
          recapId={recapId}
          publicEnabled={publicEnabled}
          beneficiaryName={beneficiaryName}
          publicUrl={publicUrl}
        />
      </Container>
    </PublishingContext.Provider>
  )
}
